import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  type QueryDocumentSnapshot,
  type DocumentData,
} from "firebase/firestore";
import { useAuth } from "@/contexts/AuthContext";
import { logError } from "@/shared/services/ErrorReporting";
import { toast } from "@/utils/toast";
import { db } from "@/integrations/firebase/firestore";
import type { SharedJob } from "@/components/Sharing/types";

const toSharedJob = (
  doc: QueryDocumentSnapshot<DocumentData>,
  viewerEmail: string,
): SharedJob => {
  const data = doc.data();
  const emails: string[] = data.collaborator_emails ?? [];
  const openedEmails: string[] = data.has_opened_emails ?? [];
  return {
    jobId: doc.id,
    summaryId: data.summary_id,
    ownerEmail: data.owner_email,
    manuscriptTitle: data.manuscript_title,
    manuscriptAuthor: data.manuscript_author,
    fileName: data.file_name,
    desiredWordCount: data.desired_word_count,
    sharedAt: data.created_at?.toDate() ?? new Date(),
    collaborators: emails.map((email) => ({ email })),
    hasOpened: openedEmails.includes(viewerEmail),
  };
};

export const useSharedJobs = () => {
  const { authUser } = useAuth();
  const [jobs, setJobs] = useState<SharedJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (error) {
      logError(error);
      toast.error(error.message);
      setError(null);
    }
  }, [error]);

  useEffect(() => {
    const viewerEmail = authUser?.email?.toLowerCase();
    if (!viewerEmail) {
      setJobs([]);
      setIsLoading(false);
      return;
    }

    const q = query(
      collection(db, "shared_jobs"),
      where("collaborator_emails", "array-contains", viewerEmail),
      orderBy("created_at", "desc"),
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setJobs(snap.docs.map((doc) => toSharedJob(doc, viewerEmail)));
        setIsLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [authUser]);

  return { jobs, isLoading, error };
};
